import type { ContentBuilder } from '../../ui/ContentBuilder';
import type { HexMapState } from '../../../engine/render/renderer';
import type { SettlementView } from '../utils';
import { t } from '../../../i18n/translations';
import type { Language } from '../../../i18n/translations';
import { getResourceIconId } from '../../../ui/overlay/assets/resourceIcons';
import {
    BoxItem,
    GridLayoutItem,
    LinearLayoutItem,
    TextItem,
    ThemedIconItem,
    type UiItem,
} from '../../ui/items';

type StorageEntry = {
    key: keyof Omit<SettlementView['inventory'], 'tools'> | 'tools';
    value: number;
};

type StorageGridEntry = {
    label: string;
    iconId?: string | null;
    value: number;
    produced: number;
    consumed: number;
};

function buildStorageCells(entries: StorageGridEntry[]): UiItem[] {
    return entries.map((entry) => {
        const labelItems: UiItem[] = [];
        if (entry.iconId) {
            labelItems.push(new ThemedIconItem(entry.iconId, (t) => t.storageGrid.iconSize));
        }
        labelItems.push(new TextItem(entry.label, 'muted'));

        const labelRow = new LinearLayoutItem(labelItems, {
            orientation: 'horizontal',
            gap: 4,
            width: 'fill',
            crossGravity: 'center',
        });

        const leftColumn = new LinearLayoutItem(
            [labelRow, new TextItem(`${entry.value}`, 'body')],
            {
                orientation: 'vertical',
                gap: 0,
                width: 'fill',
            },
        );

        const rightColumn = new LinearLayoutItem(
            [
                new TextItem(`+${entry.produced.toFixed(1)}`, 'muted', {
                    color: (t) => t.colors.barGood,
                    align: 'right',
                }),
                new TextItem(`-${entry.consumed.toFixed(1)}`, 'muted', {
                    color: (t) => t.colors.barWarn,
                    align: 'right',
                }),
            ],
            {
                orientation: 'vertical',
                gap: 0,
                width: 'wrap',
                crossGravity: 'end',
            },
        );

        const row = new LinearLayoutItem([leftColumn, rightColumn], {
            orientation: 'horizontal',
            gap: 0,
            width: 'fill',
            weights: [1, 0],
            crossGravity: 'start',
        });

        return new BoxItem(row, {
            padding: (t) => t.storageGrid.padding,
            fill: (t) => t.colors.tabInactiveFill,
            stroke: (t) => t.colors.tabStroke,
            strokeWidth: 1,
            widthMode: 'fill',
        });
    });
}

const STORAGE_ORDER: StorageEntry['key'][] = [
    'food',
    'fish',
    'wood',
    'planks',
    'stone',
    'bricks',
    'charcoal',
    'ironOre',
    'ironIngots',
    'fiber',
    'clothRope',
    'leather',
    'weapons',
    'goldOre',
    'coins',
    'tools',
];

function sumValues(values: number[]): number {
    return values.reduce((sum, value) => sum + value, 0);
}

function formatRate(value: number): number {
    return Math.round(value * 10) / 10;
}

export function renderStorageTab(
    builder: ContentBuilder,
    state: HexMapState,
    settlement: SettlementView,
    lang: Language,
): void {
    const statsMinutes = state.config.ui.storageStatsMinutes;
    builder.section(t(lang, 'buildings.storage'), (section) => {
        const entries = STORAGE_ORDER.map((key) => {
            const value =
                key === 'tools' ? settlement.inventory.tools.length : settlement.inventory[key];
            const stats = settlement.resourceStats;
            const producedTotal = stats ? sumValues(stats.produced[key]) : 0;
            const consumedTotal = stats ? sumValues(stats.consumed[key]) : 0;
            const produced = formatRate(producedTotal / statsMinutes);
            const consumed = formatRate(consumedTotal / statsMinutes);
            return {
                label: t(lang, `resource.${key}`),
                iconId: getResourceIconId(key),
                value,
                produced,
                consumed,
            };
        });
        const cells = buildStorageCells(entries);
        section.item(
            new GridLayoutItem(cells, {
                columns: (t) => t.storageGrid.columns,
                gap: (t) => t.storageGrid.gap,
                padding: 0,
                childWidthMode: 'fill',
                alignY: 'start',
            }),
        );
    });
}
